import CtaButtonGroup, { CtaType } from "@/components/molecules/CtaButtonGroup";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faStar } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Image from "next/image";
import Text from "./shared/Text";

/**
 * ReviewsMarqueeWithPainPointsSection — competitor-alternative page section
 * that surfaces 1-star reviews of a competitor on a horizontal marquee rail,
 * followed by a 5-column grid of pain points.
 *
 * Anatomy:
 *   peach/pink wash background · centered split header + CTA · looping
 *   marquee of review cards (pauses on hover) · 5-up pain-point row.
 */

export type ReviewCard = {
  author: string;
  country: string;
  reviewCount?: string;
  rating: number;
  title: string;
  body: string;
  date: string;
  dateLabel?: string;
  label?: string;
  avatarBg?: string;
};

export type PainPoint = {
  icon: IconDefinition;
  heading: string;
  description: string;
};

export type ReviewsMarqueeWithPainPointsData = {
  titleMuted: string;
  titleBright: string;
  subtitle?: string;
  primaryCta: CtaType;
  ctas?: CtaType[];
  reviews: ReviewCard[];
  /** When set, renders these images side-by-side instead of the scrolling marquee. */
  reviewImages?: string[];
  /** When set, renders these ReviewCard objects in a static 2-column grid instead of the scrolling marquee. */
  staticReviews?: ReviewCard[];
  painPoints: PainPoint[];
};

const initials = (name: string) =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map((n) => n.charAt(0).toUpperCase())
    .join("");

const StarsRow = ({ rating }: { rating: number }) => (
  <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <span
        key={i}
        className={`inline-flex h-4 w-4 items-center justify-center ${
          i < rating ? "bg-[#F97316]" : "bg-neutralBlack/10"
        }`}
        aria-hidden="true"
      >
        <FontAwesomeIcon
          icon={faStar}
          className="h-2.5 w-2.5 text-white"
        />
      </span>
    ))}
  </div>
);

const ReviewCardView = ({ review, className, noClamp }: { review: ReviewCard; className?: string; noClamp?: boolean }) => (
  <article className={`flex shrink-0 flex-col justify-between rounded-2xl border border-neutralBlack/5 bg-white p-5 shadow-[0_6px_20px_rgba(20,20,40,0.05)] ${className ?? "h-[380px] w-[340px] sm:w-[360px] md:w-[380px]"}`}>
    <div>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div
            className={`flex h-9 w-9 items-center justify-center rounded-full text-[12px] font-bold text-white ${
              review.avatarBg ?? "bg-[#5766F2]"
            }`}
            aria-hidden="true"
          >
            {initials(review.author)}
          </div>
          <div>
            <Text
              as="div"
              variant="body-md"
              className="text-[13px] font-semibold text-neutralBlack"
            >
              {review.author}
            </Text>
            <Text
              as="div"
              variant="body-sm"
              className="text-[12px] text-darkSlate04"
            >
              {review.country}
              {review.reviewCount ? ` · ${review.reviewCount}` : ""}
            </Text>
          </div>
        </div>
        <Text
          as="div"
          variant="body-sm"
          className="shrink-0 text-[12px] text-darkSlate04"
        >
          {review.date}
        </Text>
      </div>

      <div className="mt-4">
        <StarsRow rating={review.rating} />
      </div>

      <Text
        as="h4"
        variant="title-sm"
        className="mt-3 text-[16px] font-semibold text-neutralBlack"
      >
        {review.title}
      </Text>
      <Text
        as="p"
        variant="body-md"
        className={`mt-2 text-[13px] leading-relaxed text-darkSlate04 ${noClamp ? "" : "line-clamp-6"}`}
      >
        {review.body}
      </Text>
    </div>
    {noClamp ? (
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="rounded-md bg-[#F3F4F6] px-2.5 py-1 text-[12px] text-darkSlate04">
          {review.dateLabel ?? review.date}
        </span>
        {review.label ? (
          <span className="rounded-md bg-[#F3F4F6] px-2.5 py-1 text-[12px] text-darkSlate04">
            {review.label}
          </span>
        ) : null}
      </div>
    ) : (
      <div className="mt-4 flex items-center justify-between">
        <Text variant="body-sm" className="text-[12px] text-darkSlate04">
          {review.dateLabel ?? `Date of experience: ${review.date}`}
        </Text>
        {review.label ? (
          <Text variant="body-sm" className="text-[12px] text-darkSlate04">
            {review.label}
          </Text>
        ) : null}
      </div>
    )}
  </article>
);

const ReviewsMarqueeWithPainPointsSection = ({
  titleMuted,
  titleBright,
  subtitle,
  primaryCta,
  ctas,
  reviews,
  reviewImages,
  staticReviews,
  painPoints,
}: ReviewsMarqueeWithPainPointsData) => {
  const loop = [...reviews, ...reviews];
  return (
    <section className="relative w-full overflow-hidden bg-white">
      {/* Peach/pink wash */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          background:
            "radial-gradient(ellipse 70% 90% at 0% 0%, rgba(255, 180, 210, 0.22) 0%, rgba(255, 255, 255, 0) 75%)," +
            "radial-gradient(ellipse 70% 90% at 100% 100%, rgba(255, 210, 160, 0.2) 0%, rgba(255, 255, 255, 0) 75%)",
          maskImage:
            "linear-gradient(to bottom, transparent 0%, black 15%, black 85%, transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent 0%, black 15%, black 85%, transparent 100%)",
        }}
      />

      <div className="relative z-10 py-10 md:py-20 lg:py-24">
        <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
        <div className="mx-auto max-w-6xl">
          <Text
            as="h2"
            variant="display-lg"
            className="text-center text-[28px] sm:text-[32px] md:text-[40px] lg:text-[48px]"
          >
            <span className="text-neutralBlack">{titleMuted}</span>
            <br />
            <span className="text-neutralBlack">{titleBright}</span>
          </Text>
          {subtitle ? (
            <Text
              as="p"
              variant="body-lg"
              className="mx-auto mt-5 max-w-3xl text-center text-darkSlate04"
            >
              {subtitle}
            </Text>
          ) : null}
          <div className="mt-8 flex items-center justify-center md:mt-10">
            <CtaButtonGroup
              ctas={ctas ?? [primaryCta]}
              gap="flex gap-3"
              fullWidthOnMobile={false}
            />
          </div>
        </div>
      </div>

        {/* Static review images (opt-in) or static review cards or scrolling marquee */}
        {staticReviews && staticReviews.length > 0 ? (
          <div className="container mx-auto mt-12 px-4 sm:px-6 md:mt-16 md:px-8 lg:px-12">
            <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 md:grid-cols-2">
              {staticReviews.map((r, i) => (
                <ReviewCardView key={i} review={r} className="w-full h-auto min-h-[320px]" noClamp />
              ))}
            </div>
          </div>
        ) : reviewImages && reviewImages.length > 0 ? (
          <div className="container mx-auto mt-12 px-4 sm:px-6 md:mt-16 md:px-8 lg:px-12">
            <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 md:grid-cols-2">
              {reviewImages.map((src, i) => (
                <div key={i} className="overflow-hidden rounded-2xl border border-neutralBlack/5 shadow-[0_6px_20px_rgba(20,20,40,0.05)]">
                  <Image
                    src={src}
                    alt={`Review ${i + 1}`}
                    width={720}
                    height={480}
                    className="h-auto w-full object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="group relative mt-12 w-full md:mt-16">
            <div className="flex w-max gap-4 pb-6 pl-4 pr-4 animate-[feature-marquee-left_60s_linear_infinite] group-hover:[animation-play-state:paused] sm:gap-5 sm:pl-6 sm:pr-6 md:gap-6">
              {loop.map((r, i) => (
                <ReviewCardView key={`${r.author}-${i}`} review={r} />
              ))}
            </div>
          </div>
        )}

        {/* Pain points grid */}
        {painPoints && painPoints.length ? (
          <div className="container mx-auto mt-10 px-4 sm:px-6 md:mt-20 md:px-8 lg:px-12">
        <div className="mx-auto max-w-6xl">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:gap-6 lg:grid-cols-6">
              {painPoints.map((p) => (
                <div key={p.heading} className="flex flex-col items-start">
                  <div className="relative h-6 w-6">
                    <FontAwesomeIcon
                      icon={p.icon}
                      className="h-6 w-6 text-[#F97316]"
                      aria-hidden="true"
                    />
                    <div
                      className="pointer-events-none absolute inset-0"
                      aria-hidden="true"
                    >
                      <div className="absolute left-1/2 top-1/2 h-[2px] w-[140%] -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-full bg-[#F97316]" />
                    </div>
                  </div>
                  <Text
                    as="h3"
                    variant="title-sm"
                    className="mt-4 text-[14px] font-semibold text-neutralBlack"
                  >
                    {p.heading}
                  </Text>
                  <Text
                    as="p"
                    variant="body-md"
                    className="mt-2 text-[12px] leading-relaxed text-darkSlate04"
                  >
                    {p.description}
                  </Text>
                </div>
              ))}
            </div>
          </div>
      </div>
        ) : null}
      </div>
    </section>
  );
};

export default ReviewsMarqueeWithPainPointsSection;
